export class ConsoleApiError extends Error {
  constructor(message, { status = 0, code = "console_api_error", requestId = null, cause = null } = {}) {
    super(message);
    this.name = "ConsoleApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    if (cause) this.cause = cause;
  }
}

async function parseJson(response) {
  try {
    return await response.json();
  } catch (cause) {
    throw new ConsoleApiError("The dashboard service returned an unreadable response.", {
      status: response.status,
      code: "invalid_response",
      cause,
    });
  }
}

export function createConsoleApi({
  fetchImpl = globalThis.fetch,
  getAccessToken = async () => null,
  getActiveClientSlug = () => null,
  getViewAsTarget = () => null,
  createIdempotencyKey = () => globalThis.crypto?.randomUUID?.(),
} = {}) {
  if (typeof fetchImpl !== "function") {
    throw new TypeError("fetch implementation is required");
  }

  async function request(path, { method = "GET", body, auth = true, omitActiveClient = false, expectClientSlug = true, binary = false, signal, headers = {} } = {}) {
    const snapshot = auth && !omitActiveClient ? getActiveClientSlug() ?? null : null;
    // Who the dashboard reads as (View as, staff only) is bound the same way: a look that starts or stops while a
    // request is in flight refuses that request's answer.
    const viewAsSnapshot = auth ? getViewAsTarget() ?? null : null;
    const guardWorkspace = () => {
      if (auth && !omitActiveClient && (getActiveClientSlug() ?? null) !== snapshot) {
        throw new ConsoleApiError("The active workspace changed. Try again in the selected workspace.", { status: 409, code: "workspace_changed" });
      }
      if (auth && (getViewAsTarget() ?? null) !== viewAsSnapshot) {
        throw new ConsoleApiError("The active workspace changed. Try again in the selected workspace.", { status: 409, code: "workspace_changed" });
      }
    };
    const requestHeaders = { Accept: "application/json", ...headers };

    if (auth) {
      const token = await getAccessToken();
      if (!token) {
        throw new ConsoleApiError("Sign in is required.", {
          status: 401,
          code: "client_auth_missing",
        });
      }
      requestHeaders.Authorization = `Bearer ${token}`;
      guardWorkspace();
      const clientSlug = snapshot;
      if (clientSlug) requestHeaders["X-Client"] = clientSlug;
      if (viewAsSnapshot) requestHeaders["X-View-As"] = viewAsSnapshot;
    }

    let serializedBody;
    if (body instanceof FormData) {
      serializedBody = body;
      delete requestHeaders["Content-Type"];
    } else if (body !== undefined) {
      requestHeaders["Content-Type"] = "application/json";
      serializedBody = JSON.stringify(body);
    }

    let response;
    try {
      response = await fetchImpl(path, {
        method,
        headers: requestHeaders,
        body: serializedBody,
        cache: "no-store",
        credentials: "same-origin",
        redirect: "error",
        signal,
      });
    } catch (cause) {
      throw new ConsoleApiError("The dashboard service is unavailable.", {
        status: 0,
        code: "network_error",
        cause,
      });
    }

    guardWorkspace();
    if (binary && response.ok) {
      if (!snapshot || response.headers.get("X-Client") !== snapshot) {
        throw new ConsoleApiError("The download did not identify the selected workspace.", {status:409,code:"tenant_payload_mismatch"});
      }
      const blob = await response.blob();
      guardWorkspace();
      return blob;
    }
    const payload = await parseJson(response);
    guardWorkspace();
    if (!response.ok) {
      throw new ConsoleApiError(
        typeof payload?.message === "string" ? payload.message : "The dashboard request failed.",
        {
          status: response.status,
          code: typeof payload?.code === "string" ? payload.code : `http_${response.status}`,
          requestId: typeof payload?.requestId === "string" ? payload.requestId : null,
        }
      );
    }

    // `expectClientSlug: false` (checkout/settings/Alloy state) only waives
    // the "response must echo a clientSlug at all" check below — a payload
    // that DOES carry a clientSlug is still checked against the active
    // workspace unconditionally, so a cross-tenant response is never silently
    // accepted just because the endpoint doesn't normally echo the tenant.
    const activeClientSlug = snapshot;
    const actualClientSlug = payload?.clientSlug ?? payload?.client?.clientSlug ?? null;
    if (activeClientSlug && expectClientSlug && !actualClientSlug) {
      throw new ConsoleApiError("The server response did not identify its client workspace.", {
        code: "tenant_response_missing",
        status: 502,
      });
    }
    if (activeClientSlug && actualClientSlug && actualClientSlug !== activeClientSlug) {
      throw new ConsoleApiError("The dashboard returned data for a different workspace.", {
        status: 409,
        code: "tenant_payload_mismatch",
      });
    }

    return payload;
  }

  // One chat turn. The reply arrives as a stream, so this hands back the raw response for the
  // caller to read; everything else on this client parses JSON and would swallow the stream. A turn in a
  // connection session passes `connect: { app }`: the server attaches the connection instructions per turn.
  async function streamAgentChatTurn({ sessionId, message, images = undefined, documents = undefined, reasoningEffort = undefined, connect = undefined, signal = undefined }) {
    // Bind the turn before token refresh: changing workspace must never reroute an old draft.
    const clientSlug = getActiveClientSlug() ?? null;
    const guardWorkspace = () => {
      if ((getActiveClientSlug() ?? null) !== clientSlug) {
        throw new ConsoleApiError("The active workspace changed. Try again in the selected workspace.", {
          status: 409, code: "workspace_changed",
        });
      }
    };
    const token = await getAccessToken();
    guardWorkspace();
    if (!token) {
      throw new ConsoleApiError("Sign in is required.", { status: 401, code: "client_auth_missing" });
    }
    const headers = {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
    if (clientSlug) headers["X-Client"] = clientSlug;
    const viewAsTarget = getViewAsTarget() ?? null;
    if (viewAsTarget) headers["X-View-As"] = viewAsTarget;

    let response;
    try {
      response = await fetchImpl(`/api/agent-chat/sessions/${encodeURIComponent(sessionId)}/turn`, {
        method: "POST",
        headers,
        body: JSON.stringify({ message, ...(documents?.length ? { documents: documents.map(({ name, mimeType, base64 }) => ({ name, mimeType, base64 })) } : {}), ...(images?.length ? { images: images.map(({ name, dataUrl }) => ({ name, dataUrl })) } : {}), ...(reasoningEffort == null ? {} : { reasoningEffort }), ...(connect ? { connect: { app: connect.app } } : {}) }),
        cache: "no-store",
        credentials: "same-origin",
        redirect: "error",
        signal,
      });
    } catch (cause) {
      if (cause?.name === "AbortError") throw cause;
      throw new ConsoleApiError("The dashboard service is unavailable.", { status: 0, code: "network_error", cause });
    }
    guardWorkspace();
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      guardWorkspace();
      throw new ConsoleApiError(
        typeof payload?.message === "string" ? payload.message : "The agent could not be reached.",
        { status: response.status, code: typeof payload?.code === "string" ? payload.code : `http_${response.status}` },
      );
    }
    return response;
  }

  return {
    getPublicConfig: () => request("/api/config", { auth: false }),
    // Every brand the person belongs to on one line each (the All brands page), or the ones `clients` names. The answer
    // is read under the active workspace like any page read and carries each brand's own slug on its row.
    getBrands: (clients = []) => request(`/api/brands${clients.length ? `?clients=${encodeURIComponent(clients.join(","))}` : ""}`),
    getAgentChat: () => request("/api/agent-chat"),
    getAgentChatThinking: () => request("/api/agent-chat/thinking"),
    // `body` is { seed: { kind: "connect", app } } for a connection session, and nothing for an ordinary chat; `app` is
    // any app the Add panel offers, or the name of one no catalog knows (^[a-z0-9][a-z0-9_-]{1,39}$, from Something
    // else). A connection session answers with what the dashboard prepared on `session.seed`: { kind, app, label, flow,
    // fields, link, linkKind, flowId, note, verified, method, alternatives }. The session starts on the best way that
    // works from the dashboard (`method`: "mcp" | "pipedream" | "higgsfield" | "key" | "agent_browser" | "custom").
    // `link` is the sign-in to open (in a window, `linkKind: "popup"`) for an app that signs in, `flowId` the MCP
    // sign-in to watch with getMcpFlow, `fields` the key fields for an app added by key (for "custom", one field whose
    // `envKey` is the name to start the editable name with, installed with addCustomAgentConnection), and `note` what to
    // tell the person beside it: why there is no link, or that the link could not be checked. `verified` is true for a
    // link known to work (a native MCP sign-in the provider took when the dashboard opened it, or a Pipedream or
    // Higgsfield link the dashboard's own services made), false for a native MCP sign-in that could not be checked or
    // that the provider refused, and null where there is no link. With "agent_browser" there is no link: the agent signs
    // in itself, in its own browser, with the login the person gives it in the chat. `alternatives` are the other ways,
    // best first, each a button beside the chat: [{ kind: "pipedream" | "key" | "agent_browser", label, fields? }] (a
    // Pipedream sign-in is minted with startConnection when picked; a key carries its own fields). A Higgsfield link is
    // finished with completeHiggsfieldConnection, as on the page.
    createAgentChatSession: (body) => request("/api/agent-chat/sessions", { method: "POST", ...(body ? { body } : {}) }),
    getAgentChatSession: (sessionId) => request(`/api/agent-chat/sessions/${encodeURIComponent(sessionId)}`),
    getAgentConversations: ({ cursor } = {}) => request(`/api/agent/conversations${cursor == null ? "" : `?cursor=${encodeURIComponent(cursor)}`}`),
    getAgentConversationHistory: ({ offset = 0 } = {}) => request(`/api/agent/conversations/history?offset=${encodeURIComponent(offset)}`),
    getAgentConversationUsage: ({ sessionId, cursor } = {}) => request(`/api/agent/conversations/usage?sessionId=${encodeURIComponent(sessionId)}${cursor == null ? "" : `&cursor=${encodeURIComponent(cursor)}`}`),
    getAgentConversation: ({ sessionId, offset = 0 } = {}) => request(`/api/agent/conversations/transcript/${encodeURIComponent(sessionId)}?offset=${encodeURIComponent(offset)}`),
    getAgentSessions: () => request("/api/agent/sessions"),
    getAgentSession: (sessionId) => request(`/api/agent/sessions/${encodeURIComponent(sessionId)}`),
    getKnowledgeUploads: () => request("/api/agent/knowledge/uploads"),
    uploadKnowledgeFile: (file, category = "Documents") => {
      const body = new FormData(); body.append("file", file); body.append("category", category);
      return request("/api/agent/knowledge/uploads", {method:"POST", body});
    },
    downloadKnowledgeUpload: (id) => request(`/api/agent/knowledge/uploads/${encodeURIComponent(id)}/download`, {binary:true}),
    removeKnowledgeUpload: (id) => request(`/api/agent/knowledge/uploads/${encodeURIComponent(id)}`, {method:"DELETE"}),
    getBrainAtlas: (options = {}) => {
      const params = new URLSearchParams();
      for (const key of ["domain", "q", "offset"]) {
        if (options[key] !== undefined && options[key] !== null && options[key] !== "") params.set(key, String(options[key]));
      }
      return request(`/api/agent/brain/atlas${params.size ? `?${params}` : ""}`);
    },
    getBrainPlaybook: (id) => request(`/api/agent/brain/playbooks/${encodeURIComponent(id)}`),
    getAgentBrain: () => request("/api/agent/brain"),
    getAgentFiles: () => request("/api/agent/files"),
    getAgentFile: (fileId) => request(`/api/agent/files/${encodeURIComponent(fileId)}`),
    getAgentAutomations: () => request("/api/agent/automations"),
    getBrain: (view) => request(`/api/brain?view=${encodeURIComponent(view)}`),
    getBrainEntry: (view, entryId) => request(`/api/brain/${encodeURIComponent(entryId)}?view=${encodeURIComponent(view)}`),
    renameAgentChatSession: (sessionId, title) => request(`/api/agent-chat/sessions/${encodeURIComponent(sessionId)}`, {
      method: "PATCH",
      body: { title },
    }),
    archiveAgentChatSession: (sessionId) => request(`/api/agent-chat/sessions/${encodeURIComponent(sessionId)}`, {
      method: "PATCH",
      body: { archived: true },
    }),
    // Asks the agent to stop the turn it is writing. `runId` is the run the stream named; with none (a click before the
    // stream's first event) the body is {} and the agent stops whatever run the session has active. Answers { clientSlug,
    // sessionId, runId, status }, where `status` is "stopping", or "idle" when the turn had already ended.
    stopAgentChatTurn: ({ sessionId, runId }) => request(`/api/agent-chat/sessions/${encodeURIComponent(sessionId)}/stop`, {
      method: "POST",
      body: { runId },
    }),
    streamAgentChatTurn,
    getMe: ({ omitActiveClient = false } = {}) => request("/api/me", { omitActiveClient }),
    // The active brand's people, for View as. Staff only: everyone else is answered 403.
    getViewAsMembers: () => request("/api/view-as/members"),
    getHome: () => request("/api/home"),
    getActivity: () => request("/api/activity"),
    getWork: () => request("/api/work"),
    getWorkV2: () => request("/api/work/v2"),
    saveWorkPreferences: (work) => request("/api/work/v2/preferences", {
      method: "PUT",
      body: { work },
    }),
    createWorkAction: ({ actionType, intent, answers, entityRefs = [], idempotencyKey = null }) => {
      const requestKey = idempotencyKey || createIdempotencyKey();
      if (!requestKey) {
        throw new ConsoleApiError("This browser cannot create a safe action identifier.", {
          code: "idempotency_unavailable",
        });
      }
      return request("/api/work/v2/actions", {
        method: "POST",
        body: { actionType, intent, answers, entityRefs },
        headers: { "Idempotency-Key": requestKey },
      });
    },
    getLearningSystem: () => request("/api/work/learning"),
    rateLearningSubject: ({ subjectKind, subjectId, rating, note = null }) => {
      const requestKey = createIdempotencyKey();
      if (!requestKey) {
        throw new ConsoleApiError("This browser cannot create a safe rating identifier.", {
          code: "idempotency_unavailable",
        });
      }
      return request("/api/work/learning/ratings", {
        method: "POST",
        body: { subjectKind, subjectId, rating, note },
        headers: { "Idempotency-Key": requestKey },
      });
    },
    queueLearningSuggestion: (suggestionId) => {
      const requestKey = createIdempotencyKey();
      if (!requestKey) {
        throw new ConsoleApiError("This browser cannot create a safe queue identifier.", {
          code: "idempotency_unavailable",
        });
      }
      return request(`/api/work/learning/suggestions/${encodeURIComponent(suggestionId)}/queue`, {
        method: "POST",
        body: {},
        headers: { "Idempotency-Key": requestKey },
      });
    },
    muteTasteLearning: (learningId, muted) => {
      const requestKey = createIdempotencyKey();
      if (!requestKey) {
        throw new ConsoleApiError("This browser cannot create a safe learning identifier.", {
          code: "idempotency_unavailable",
        });
      }
      return request(`/api/work/learning/taste/${encodeURIComponent(learningId)}/mute`, {
        method: "POST",
        body: { muted },
        headers: { "Idempotency-Key": requestKey },
      });
    },
    getWorkTask: (taskId) => request(`/api/work/${encodeURIComponent(taskId)}`),
    getRequests: () => request("/api/requests"),
    getConnectors: () => request("/api/onboarding/connectors"),
    getConnections: () => request("/api/connections"),
    getAgentConnections: () => request("/api/agent-connections"),
    startShopifyConnection: () => request("/api/shopify-connection/start", { method: "POST" }),
    completeShopifyConnection: (sessionId) => request("/api/shopify-connection/complete", { method: "POST", body: { sessionId } }),
    addAgentConnection: (app, fields, extra = {}) => request(`/api/agent-connections/${encodeURIComponent(app)}`, { method: "POST", body: { fields, ...extra } }),
    addCustomAgentConnection: (envKey, value) => request("/api/agent-connections/custom", { method: "POST", body: { envKey, fields: { [envKey]: value } } }),
    // `kind` is the card the page acts on ("mcp" or "api_key"): an app can hold both, and only one is on the page.
    removeAgentConnection: (app, kind) => request(`/api/agent-connections/${encodeURIComponent(app)}${kind ? `?kind=${encodeURIComponent(kind)}` : ""}`, { method: "DELETE" }),
    testAgentConnection: (app, kind) => request(`/api/agent-connections/${encodeURIComponent(app)}/test${kind ? `?kind=${encodeURIComponent(kind)}` : ""}`, { method: "POST" }),
    connectMcp: (app) => request(`/api/agent-connections/mcp/${encodeURIComponent(app)}/connect`, { method: "POST" }),
    // The tools shared with the brand this one is hosted with, and one key copied `{ from }` the partner into
    // this brand or `{ to }` the partner from it.
    getConnectionSharing: () => request("/api/agent-connections/sharing"),
    shareAgentConnection: (app, direction) => request(`/api/agent-connections/${encodeURIComponent(app)}/share`, { method: "POST", body: direction }),
    getMcpFlow: (flowId) => request(`/api/agent-connections/mcp/flows/${encodeURIComponent(flowId)}`),
    getAgentConnectionJob: (id) => request(`/api/agent-connections/jobs/${encodeURIComponent(id)}`),
    getConnectionStatus: (appKey) => request(`/api/connections/${encodeURIComponent(appKey)}/status`),
    // A sign-in that needs nothing from the person sends no body. Google signs in through Pipedream Connect, which asks
    // for nothing up front; a Pipedream link meant for a window comes back as `connectLinkUrl` too.
    startConnection: (appKey, body = {}) => request(`/api/connections/${encodeURIComponent(appKey)}/start`, { method: "POST", ...(Object.keys(body).length ? { body } : {}) }),
    // Answers 202 while the Higgsfield sign-in link waits for approval, 200 once installing, and 410 once it expired.
    completeHiggsfieldConnection: () => request("/api/connections/higgsfield/complete", { method: "POST" }),
    reconnectConnection: (appKey, body = {}) => request(`/api/connections/${encodeURIComponent(appKey)}/reconnect`, { method: "POST", ...(Object.keys(body).length ? { body } : {}) }),
    testConnection: (appKey) => request(`/api/connections/${encodeURIComponent(appKey)}/test`, { method: "POST" }),
    disconnectConnection: (appKey) => request(`/api/connections/${encodeURIComponent(appKey)}`, { method: "DELETE" }),
    revokeConnection: (appKey) => request(`/api/connections/${encodeURIComponent(appKey)}`, { method: "DELETE" }),
    getSkills: () => request("/api/skills"),
    getTeam: () => request("/api/team"),
    getCredits: () => request("/api/credits"),
    // Usage by person on the Alloy page: the workspace's members and their opaque dashboard-chat owner keys.
    // 404 means the feature is not switched on for this workspace.
    getAlloyPeople: () => request("/api/alloy/people"),
    // Who started each of the agent's Slack conversations, matched to members by email on the server.
    // `available: false` means Slack stays one unassigned group.
    getAlloySlackPeople: () => request("/api/alloy/slack-people"),
    createCreditsCheckout: ({ amountCents, idempotencyKey = null }) => {
      const requestKey = idempotencyKey || createIdempotencyKey();
      if (!requestKey) {
        throw new ConsoleApiError("This browser cannot create a safe checkout identifier.", {
          code: "idempotency_unavailable",
        });
      }
      return request("/api/credits/checkout", {
        method: "POST",
        body: { amountCents },
        headers: { "Idempotency-Key": requestKey },
        // The BFF's checkout response is `{ checkoutUrl, amountCents }` only —
        // it does not echo the tenant's clientSlug, so the generic tenant-echo
        // check below must be skipped for this endpoint (X-Client is still sent).
        expectClientSlug: false,
      });
    },
    saveCreditsSettings: ({ autoRechargeEnabled, autoRechargePackageCents }) => request("/api/credits/settings", {
      method: "POST",
      body: { autoRechargeEnabled, autoRechargePackageCents },
      expectClientSlug: false,
    }),
    setAlloyModel: ({ model, reasoningEffort }) => request("/api/alloy/model", {
      method: "POST",
      body: { model, reasoningEffort },
      // `/api/alloy/model` answers `{ model, reapply }` with no clientSlug.
      expectClientSlug: false,
    }),
    requestAlloyState: ({ requestedState }) => request("/api/alloy", {
      method: "POST",
      body: { requestedState },
      // `/api/alloy` answers `{ requestId, status, requestedState }` with no
      // clientSlug; see the checkout note above.
      expectClientSlug: false,
    }),
    addTeamMember: ({ email, role }) => request("/api/team/members", {
      method: "POST",
      body: { email, role },
    }),
    setTeamMemberRole: ({ email, role }) => request("/api/team/members", {
      method: "PATCH",
      body: { email, role },
    }),
    revokeTeamMember: ({ email }) => request("/api/team/members/revoke", {
      method: "POST",
      body: { email },
    }),
    getAxosDashboardOffers: ({ signal } = {}) => request("/api/axos/dashboard/offers", { signal }),
    getOffers: ({ signal } = {}) => request("/api/offers", { signal }),
    getOffer: (offerKey, { sort = "spend", days = 30, signal } = {}) => request(`/api/offers/${encodeURIComponent(offerKey)}?${new URLSearchParams({ sort, days, limit: 10 })}`, { signal }),
    getOfferAsset: (kind, assetKey, { days = 30, signal } = {}) => request(`/api/offers/assets/${encodeURIComponent(kind)}/${encodeURIComponent(assetKey)}?${new URLSearchParams({ days })}`, { signal }),
    getOfferPiece: (pieceRef, { days = 30, offset = 0, signal } = {}) => request(`/api/offers/pieces/${encodeURIComponent(pieceRef)}?${new URLSearchParams({ days, limit: 20, offset })}`, { signal }),
    createOffer: (offer, { idempotencyKey, signal } = {}) => request("/api/offers", { method: "POST", body: offer, signal, headers: { "Idempotency-Key": idempotencyKey } }),
    getAutomations: () => request("/api/automations"),
    // The Test section (growth system): one read for every page, small mutations for the client's part of it.
    getGrowth: ({ start, end } = {}) => request("/api/growth" + (start != null || end != null ? "?" + new URLSearchParams({ start: start ?? "", end: end ?? "" }) : "")),
    setGrowthGoals: ({ period, goals, remove = [] }) => request("/api/growth/goals", { method: "PUT", body: { period, goals, remove } }),
    updateGrowthInitiative: (initiativeId, action) => request(`/api/growth/initiatives/${encodeURIComponent(initiativeId)}`, { method: "PATCH", body: { action } }),
    addGrowthExperiment: ({ title, hypothesis = null, channel = null, metric = null }) => request("/api/growth/experiments", { method: "POST", body: { title, hypothesis, channel, metric } }),
    answerGrowthQuestion: (questionId, answer) => request(`/api/growth/questions/${encodeURIComponent(questionId)}/answer`, { method: "POST", body: { answer } }),
    skipGrowthQuestion: (questionId) => request(`/api/growth/questions/${encodeURIComponent(questionId)}/skip`, { method: "POST" }),
    getAgentProfile: () => request("/api/agent-profile"),
    createRequest: ({ title, body = "", context = null, idempotencyKey = null }) => {
      const requestKey = idempotencyKey || createIdempotencyKey();
      if (!requestKey) {
        throw new ConsoleApiError("This browser cannot create a safe request identifier.", {
          code: "idempotency_unavailable",
        });
      }
      return request("/api/requests", {
        method: "POST",
        body: { title, body, context },
        headers: { "Idempotency-Key": requestKey },
      });
    },
    createConnectorLink: (appKey) =>
      request(`/api/onboarding/connectors/${encodeURIComponent(appKey)}/connect-link`, {
        method: "POST",
      }),
  };
}
